import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Marker, Source, useMap } from 'react-map-gl';
import { useDispatch, useSelector } from 'react-redux';
import { setCropBounds as setCropBoundsAction } from '../../store/actions/map';
import { setExportMapModal } from '../../store/actions/modals';
import { basemapSelector } from '../../store/selectors/general';
import { cropBoundsSelector } from '../../store/selectors/map';
import {
  createBoundsForAspectMode,
  DEFAULT_CROP_ASPECT_MODE,
  getAspectRatioForMode,
  isCustomAspectMode,
  pixelRectToBounds,
  resizeBoundsFromCorner,
  resizeBoundsFromEdge,
  resizeBoundsWithAspect,
  translateBoundsInViewport,
} from '../../utils/cropUtils';
import { CropAspectSelector } from './CropAspectSelector';

export const CropTool = ({ isActive, resetTrigger, onClose }) => {
  const { current: map } = useMap();
  const dispatch = useDispatch();
  const cropBounds = useSelector(cropBoundsSelector);
  const setCropBounds = useCallback(bounds => dispatch(setCropBoundsAction(bounds)), [dispatch]);
  const basemapInfo = useSelector(basemapSelector);
  const [aspectMode, setAspectMode] = useState(DEFAULT_CROP_ASPECT_MODE);
  const [markerKey, setMarkerKey] = useState(0);
  const isBoxDraggingRef = useRef(false);
  const isHandleDraggingRef = useRef(false);
  const isHandleResizeActiveRef = useRef(false);
  const globalPointerReleaseRef = useRef(null);
  const lastLngLatRef = useRef(null);
  const cropBoundsRef = useRef(cropBounds);
  const cropViewportRectRef = useRef(null);
  const rotationStartRectRef = useRef(null);
  const activeMarkerRef = useRef(null);

  const detachGlobalPointerRelease = useCallback(() => {
    const handler = globalPointerReleaseRef.current;
    if (!handler) return;

    window.removeEventListener('mouseup', handler, true);
    window.removeEventListener('pointerup', handler, true);
    window.removeEventListener('pointercancel', handler, true);
    globalPointerReleaseRef.current = null;
  }, []);

  const attachGlobalPointerRelease = useCallback(
    onRelease => {
      detachGlobalPointerRelease();

      const handler = () => {
        onRelease();
        detachGlobalPointerRelease();
      };

      globalPointerReleaseRef.current = handler;
      window.addEventListener('mouseup', handler, true);
      window.addEventListener('pointerup', handler, true);
      window.addEventListener('pointercancel', handler, true);
    },
    [detachGlobalPointerRelease],
  );

  useEffect(() => {
    cropBoundsRef.current = cropBounds;
  }, [cropBounds]);

  const startHandleDragging = useCallback(
    cursor => {
      if (!map) return;
      isHandleDraggingRef.current = true;
      map.getCanvas().style.cursor = cursor;
      if (map.dragPan?.disable) {
        map.dragPan.disable();
      }
    },
    [map],
  );

  const stopHandleDragging = useCallback(() => {
    isHandleResizeActiveRef.current = false;
    detachGlobalPointerRelease();

    if (activeMarkerRef.current) {
      activeMarkerRef.current = null;
    }

    setMarkerKey(prev => prev + 1);

    if (!map || !isHandleDraggingRef.current) return;
    isHandleDraggingRef.current = false;
    if (!isBoxDraggingRef.current) {
      map.getCanvas().style.cursor = '';
    }
    if (map.dragPan?.enable) {
      map.dragPan.enable();
    }
  }, [detachGlobalPointerRelease, map]);

  const beginHandleResize = useCallback(
    (cursor, markerEvent) => {
      isHandleResizeActiveRef.current = true;
      if (markerEvent?.target) {
        activeMarkerRef.current = markerEvent.target;
      }
      startHandleDragging(cursor);
      attachGlobalPointerRelease(stopHandleDragging);
    },
    [attachGlobalPointerRelease, startHandleDragging, stopHandleDragging],
  );

  const endHandleResize = useCallback(() => {
    stopHandleDragging();
  }, [stopHandleDragging]);

  const handleAspectModeChange = useCallback(
    mode => {
      setAspectMode(mode);
      if (!map || isCustomAspectMode(mode)) return;
      setCropBounds(createBoundsForAspectMode(map, mode));
    },
    [map, setCropBounds],
  );

  // Cleanup handle dragging when component becomes inactive
  useEffect(() => {
    if (!isActive) {
      stopHandleDragging();
    }
  }, [isActive, stopHandleDragging]);

  // Create a fresh crop box whenever crop mode is activated/re-triggered.
  useEffect(() => {
    if (!isActive || !map) {
      return;
    }

    setAspectMode(DEFAULT_CROP_ASPECT_MODE);
    setCropBounds(createBoundsForAspectMode(map, DEFAULT_CROP_ASPECT_MODE));
  }, [isActive, map, setCropBounds, resetTrigger]);

  // Allow dragging the entire crop box by grabbing the fill area (with viewport clamping).
  useEffect(() => {
    if (!isActive || !map) {
      return;
    }

    const stopDragging = () => {
      detachGlobalPointerRelease();
      if (!isBoxDraggingRef.current) return;
      isBoxDraggingRef.current = false;
      lastLngLatRef.current = null;
      if (!isHandleDraggingRef.current) {
        map.getCanvas().style.cursor = '';
      }
      if (map.dragPan?.enable && !isHandleDraggingRef.current) {
        map.dragPan.enable();
      }
    };

    const handleFillMouseDown = evt => {
      if (!cropBoundsRef.current) return;
      if (isHandleDraggingRef.current) return;
      // If the pointer event originated from a resize handle, let handle drag logic win.
      if (
        evt.originalEvent?.target?.closest?.('.crop-handle-hitbox') ||
        evt.originalEvent?.target?.closest?.('.crop-aspect-selector') ||
        evt.originalEvent?.target?.closest?.('.crop-close-button')
      ) {
        return;
      }

      isBoxDraggingRef.current = true;
      lastLngLatRef.current = evt.lngLat;
      map.getCanvas().style.cursor = 'grabbing';
      attachGlobalPointerRelease(stopDragging);

      if (map.dragPan?.disable) {
        map.dragPan.disable();
      }
      evt.preventDefault();
      evt.originalEvent?.stopPropagation?.();
    };

    const handleMouseMove = evt => {
      if (!isBoxDraggingRef.current || !lastLngLatRef.current) {
        return;
      }

      const pointer = map.project([evt.lngLat.lng, evt.lngLat.lat]);
      const lastPointer = map.project([lastLngLatRef.current.lng, lastLngLatRef.current.lat]);
      const deltaX = pointer.x - lastPointer.x;
      const deltaY = pointer.y - lastPointer.y;

      const prev = cropBoundsRef.current;
      if (!prev) return;
      setCropBounds(translateBoundsInViewport(prev, deltaX, deltaY, map));

      lastLngLatRef.current = evt.lngLat;
    };

    const handleFillMouseEnter = () => {
      if (!isBoxDraggingRef.current && !isHandleDraggingRef.current) {
        map.getCanvas().style.cursor = 'move';
      }
    };

    const handleFillMouseLeave = () => {
      if (!isBoxDraggingRef.current && !isHandleDraggingRef.current) {
        map.getCanvas().style.cursor = '';
      }
    };

    map.on('mousedown', 'crop-tool-fill', handleFillMouseDown);
    map.on('mousemove', handleMouseMove);
    map.on('mouseup', stopDragging);
    map.on('mouseleave', stopDragging);
    map.on('mouseenter', 'crop-tool-fill', handleFillMouseEnter);
    map.on('mouseleave', 'crop-tool-fill', handleFillMouseLeave);

    return () => {
      map.off('mousedown', 'crop-tool-fill', handleFillMouseDown);
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', stopDragging);
      map.off('mouseleave', stopDragging);
      map.off('mouseenter', 'crop-tool-fill', handleFillMouseEnter);
      map.off('mouseleave', 'crop-tool-fill', handleFillMouseLeave);
      detachGlobalPointerRelease();
      stopHandleDragging();
      stopDragging();
    };
  }, [
    attachGlobalPointerRelease,
    detachGlobalPointerRelease,
    isActive,
    map,
    setCropBounds,
    stopHandleDragging,
  ]);

  // Build the polygon used by the map source/layers from the current bounds.
  const cropViewportRect = useMemo(() => {
    if (!cropBounds || !map) {
      return null;
    }

    const northWest = map.project([cropBounds.west, cropBounds.north]);
    const southEast = map.project([cropBounds.east, cropBounds.south]);

    return {
      left: Math.min(northWest.x, southEast.x),
      right: Math.max(northWest.x, southEast.x),
      top: Math.min(northWest.y, southEast.y),
      bottom: Math.max(northWest.y, southEast.y),
    };
  }, [cropBounds, map]);

  useEffect(() => {
    cropViewportRectRef.current = cropViewportRect;
  }, [cropViewportRect]);

  // Preserve the on-screen crop rectangle across map rotation.
  useEffect(() => {
    if (!isActive || !map) {
      return;
    }

    const handleRotateStart = () => {
      rotationStartRectRef.current = cropViewportRectRef.current;
    };

    const handleRotateEnd = () => {
      const lockedRect = rotationStartRectRef.current;
      rotationStartRectRef.current = null;
      if (!lockedRect) return;
      setCropBounds(pixelRectToBounds(lockedRect, map));
    };

    map.on('rotatestart', handleRotateStart);
    map.on('rotateend', handleRotateEnd);

    return () => {
      map.off('rotatestart', handleRotateStart);
      map.off('rotateend', handleRotateEnd);
    };
  }, [isActive, map, setCropBounds]);

  const cropGeoJson = useMemo(() => {
    if (!cropViewportRect || !map) {
      return null;
    }

    const topLeft = map.unproject([cropViewportRect.left, cropViewportRect.top]);
    const topRight = map.unproject([cropViewportRect.right, cropViewportRect.top]);
    const bottomRight = map.unproject([cropViewportRect.right, cropViewportRect.bottom]);
    const bottomLeft = map.unproject([cropViewportRect.left, cropViewportRect.bottom]);

    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [bottomLeft.lng, bottomLeft.lat],
            [bottomRight.lng, bottomRight.lat],
            [topRight.lng, topRight.lat],
            [topLeft.lng, topLeft.lat],
            [bottomLeft.lng, bottomLeft.lat],
          ],
        ],
      },
    };
  }, [cropViewportRect, map]);

  const isCustomMode = isCustomAspectMode(aspectMode);

  const snapCornerMarker = (evt, bounds, corner) => {
    if (!evt.target?.setLngLat || !isHandleResizeActiveRef.current) return;

    const cornerPositions = {
      nw: [bounds.west, bounds.north],
      ne: [bounds.east, bounds.north],
      se: [bounds.east, bounds.south],
      sw: [bounds.west, bounds.south],
    };

    evt.target.setLngLat(cornerPositions[corner]);
  };

  const handleAspectLockedCornerDrag = (corner, evt) => {
    if (!isHandleResizeActiveRef.current) return;

    const activeBounds = cropBoundsRef.current;
    if (!activeBounds || !map) return;

    const aspectRatio = getAspectRatioForMode(aspectMode);
    const pointer = map.project([evt.lngLat.lng, evt.lngLat.lat]);
    const nextBounds = resizeBoundsWithAspect(activeBounds, corner, pointer, map, aspectRatio);

    setCropBounds(nextBounds);
    snapCornerMarker(evt, nextBounds, corner);
  };

  const snapEdgeMarker = (evt, bounds, edge) => {
    if (!evt.target?.setLngLat || !isHandleResizeActiveRef.current) return;

    const edgePositions = {
      north: [(bounds.west + bounds.east) / 2, bounds.north],
      south: [(bounds.west + bounds.east) / 2, bounds.south],
      east: [bounds.east, (bounds.north + bounds.south) / 2],
      west: [bounds.west, (bounds.north + bounds.south) / 2],
    };

    evt.target.setLngLat(edgePositions[edge]);
  };

  const handleCustomCornerDrag = (corner, evt) => {
    if (!isHandleResizeActiveRef.current) return;

    const activeBounds = cropBoundsRef.current;
    if (!activeBounds || !map) return;

    const pointer = map.project([evt.lngLat.lng, evt.lngLat.lat]);
    const nextBounds = resizeBoundsFromCorner(activeBounds, corner, pointer, map);

    setCropBounds(nextBounds);
    snapCornerMarker(evt, nextBounds, corner);
  };

  // Corner handle resize behavior.
  const handleSouthEastDrag = evt => {
    if (!isCustomMode) {
      handleAspectLockedCornerDrag('se', evt);
      return;
    }

    handleCustomCornerDrag('se', evt);
  };

  const handleNorthWestDrag = evt => {
    if (!isCustomMode) {
      handleAspectLockedCornerDrag('nw', evt);
      return;
    }

    handleCustomCornerDrag('nw', evt);
  };

  const handleNorthEastDrag = evt => {
    if (!isCustomMode) {
      handleAspectLockedCornerDrag('ne', evt);
      return;
    }

    handleCustomCornerDrag('ne', evt);
  };

  const handleSouthWestDrag = evt => {
    if (!isCustomMode) {
      handleAspectLockedCornerDrag('sw', evt);
      return;
    }

    handleCustomCornerDrag('sw', evt);
  };

  const handleCustomEdgeDrag = (edge, evt) => {
    if (!isHandleResizeActiveRef.current) return;

    const activeBounds = cropBoundsRef.current;
    if (!activeBounds || !map) return;

    const pointer = map.project([evt.lngLat.lng, evt.lngLat.lat]);
    const nextBounds = resizeBoundsFromEdge(activeBounds, edge, pointer, map);

    setCropBounds(nextBounds);
    snapEdgeMarker(evt, nextBounds, edge);
  };

  // Edge handle resize behavior.
  const handleNorthEdgeDrag = evt => {
    handleCustomEdgeDrag('north', evt);
  };

  const handleSouthEdgeDrag = evt => {
    handleCustomEdgeDrag('south', evt);
  };

  const handleEastEdgeDrag = evt => {
    handleCustomEdgeDrag('east', evt);
  };

  const handleWestEdgeDrag = evt => {
    handleCustomEdgeDrag('west', evt);
  };

  return (
    <>
      {isActive && cropGeoJson && (
        <>
          <Source id="crop-tool-source" type="geojson" data={cropGeoJson}>
            <Layer
              id="crop-tool-fill"
              type="fill"
              paint={{
                'fill-color': '#facc15',
                'fill-opacity': 0.08,
              }}
            />
            <Layer
              id="crop-tool-outline"
              type="line"
              paint={{
                'line-color': '#eab308',
                'line-width': 2,
              }}
            />
          </Source>
          {cropViewportRect && (
            <>
              <Marker
                longitude={cropBounds.west}
                latitude={cropBounds.north}
                anchor="top-left"
                offset={[6, 6]}
              >
                <CropAspectSelector
                  aspectMode={aspectMode}
                  onAspectModeChange={handleAspectModeChange}
                />
              </Marker>
              <Marker
                longitude={cropBounds.east}
                latitude={cropBounds.north}
                anchor="top-right"
                offset={[-6, 6]}
              >
                <button
                  type="button"
                  className="crop-close-button pointer-events-auto flex h-5 w-5 items-center justify-center rounded border border-yellow-700 bg-yellow text-xs font-bold text-gray-800 shadow-sm hover:brightness-95"
                  onMouseDown={evt => {
                    evt.preventDefault();
                    evt.stopPropagation();
                  }}
                  onClick={evt => {
                    evt.preventDefault();
                    evt.stopPropagation();
                    onClose?.();
                  }}
                  aria-label="Close crop tool"
                >
                  ×
                </button>
              </Marker>
              <Marker
                longitude={(cropBounds.west + cropBounds.east) / 2}
                latitude={cropBounds.south}
                anchor="bottom"
                offset={[0, 44]}
              >
                <button
                  type="button"
                  className={clsx(
                    'crop-close-button pointer-events-auto flex items-center justify-center gap-2 rounded px-4 py-2 text-xs font-bold shadow-sm hover:bg-slateBlue hover:brightness-95',
                    basemapInfo === 'light'
                      ? 'bg-blue text-white'
                      : 'border border-slateBlue bg-white text-blue hover:text-white',
                  )}
                  onMouseDown={evt => {
                    evt.preventDefault();
                    evt.stopPropagation();
                  }}
                  onClick={evt => {
                    evt.preventDefault();
                    evt.stopPropagation();
                    dispatch(setExportMapModal(true));
                  }}
                  aria-label="Export this section"
                >
                  Export selection
                  <ArrowDownTrayIcon className="h-4 w-4" />
                </button>
              </Marker>
              {isCustomMode && (
                <>
                  <Marker
                    key={`n-edge-${markerKey}`}
                    longitude={(cropBounds.west + cropBounds.east) / 2}
                    latitude={cropBounds.north}
                    draggable
                    onDragStart={evt => beginHandleResize('n-resize', evt)}
                    onDragEnd={endHandleResize}
                    onDrag={handleNorthEdgeDrag}
                  >
                    <div className="crop-handle-hitbox flex h-8 w-12 cursor-n-resize items-center justify-center">
                      <div className="h-2 w-8 rounded-full border border-yellow-700 bg-yellow-300 shadow" />
                    </div>
                  </Marker>
                  <Marker
                    key={`s-edge-${markerKey}`}
                    longitude={(cropBounds.west + cropBounds.east) / 2}
                    latitude={cropBounds.south}
                    draggable
                    onDragStart={evt => beginHandleResize('s-resize', evt)}
                    onDragEnd={endHandleResize}
                    onDrag={handleSouthEdgeDrag}
                  >
                    <div className="crop-handle-hitbox flex h-8 w-12 cursor-s-resize items-center justify-center">
                      <div className="h-2 w-8 rounded-full border border-yellow-700 bg-yellow-300 shadow" />
                    </div>
                  </Marker>
                  <Marker
                    key={`e-edge-${markerKey}`}
                    longitude={cropBounds.east}
                    latitude={(cropBounds.north + cropBounds.south) / 2}
                    draggable
                    onDragStart={evt => beginHandleResize('e-resize', evt)}
                    onDragEnd={endHandleResize}
                    onDrag={handleEastEdgeDrag}
                  >
                    <div className="crop-handle-hitbox flex h-12 w-8 cursor-e-resize items-center justify-center">
                      <div className="h-8 w-2 rounded-full border border-yellow-700 bg-yellow-300 shadow" />
                    </div>
                  </Marker>
                  <Marker
                    key={`w-edge-${markerKey}`}
                    longitude={cropBounds.west}
                    latitude={(cropBounds.north + cropBounds.south) / 2}
                    draggable
                    onDragStart={evt => beginHandleResize('w-resize', evt)}
                    onDragEnd={endHandleResize}
                    onDrag={handleWestEdgeDrag}
                  >
                    <div className="crop-handle-hitbox flex h-12 w-8 cursor-w-resize items-center justify-center">
                      <div className="h-8 w-2 rounded-full border border-yellow-700 bg-yellow-300 shadow" />
                    </div>
                  </Marker>
                </>
              )}
              <Marker
                key={`se-corner-${markerKey}`}
                longitude={cropBounds.east}
                latitude={cropBounds.south}
                draggable
                onDragStart={evt => beginHandleResize('se-resize', evt)}
                onDragEnd={endHandleResize}
                onDrag={handleSouthEastDrag}
              >
                <div className="crop-handle-hitbox flex h-8 w-8 cursor-se-resize items-center justify-center">
                  <div className="h-3 w-3 rounded-full border border-yellow-700 bg-yellow-300 shadow" />
                </div>
              </Marker>
              <Marker
                key={`nw-corner-${markerKey}`}
                longitude={cropBounds.west}
                latitude={cropBounds.north}
                draggable
                onDragStart={evt => beginHandleResize('nw-resize', evt)}
                onDragEnd={endHandleResize}
                onDrag={handleNorthWestDrag}
              >
                <div className="crop-handle-hitbox flex h-8 w-8 cursor-nw-resize items-center justify-center">
                  <div className="h-3 w-3 rounded-full border border-yellow-700 bg-yellow-300 shadow" />
                </div>
              </Marker>
              <Marker
                key={`ne-corner-${markerKey}`}
                longitude={cropBounds.east}
                latitude={cropBounds.north}
                draggable
                onDragStart={evt => beginHandleResize('ne-resize', evt)}
                onDragEnd={endHandleResize}
                onDrag={handleNorthEastDrag}
              >
                <div className="crop-handle-hitbox flex h-8 w-8 cursor-ne-resize items-center justify-center">
                  <div className="h-3 w-3 rounded-full border border-yellow-700 bg-yellow-300 shadow" />
                </div>
              </Marker>
              <Marker
                key={`sw-corner-${markerKey}`}
                longitude={cropBounds.west}
                latitude={cropBounds.south}
                draggable
                onDragStart={evt => beginHandleResize('sw-resize', evt)}
                onDragEnd={endHandleResize}
                onDrag={handleSouthWestDrag}
              >
                <div className="crop-handle-hitbox flex h-8 w-8 cursor-sw-resize items-center justify-center">
                  <div className="h-3 w-3 rounded-full border border-yellow-700 bg-yellow-300 shadow" />
                </div>
              </Marker>
            </>
          )}
        </>
      )}
    </>
  );
};
